import { useState, useEffect, useRef } from 'react';
import { DatosSesion } from '../../Components/Common/DatosSesion';
import { getLayoutDashboard } from '../../helpers/backend_helper';

// Caché en memoria compartida entre todos los componentes
const layoutCache = {
  data: null,
  timestamp: null,
  loading: false,
  promise: null
};

const CACHE_DURATION = 5 * 60 * 1000; // 5 minutos

/**
 * Hook para cargar el layout del dashboard con caché en memoria
 * Evita llamadas duplicadas al API cuando múltiples componentes se montan simultáneamente
 */
export const useLayoutCache = (componentName = 'Component') => {
  const [layout, setLayout] = useState(null);
  const [loading, setLoading] = useState(true);
  const isMounted = useRef(true);

  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []);

  useEffect(() => {
    const loadLayout = async () => {
      const farmacia = DatosSesion("farmacia");
      const id = DatosSesion("id");
      const correo = DatosSesion("correo");
      const identificador = farmacia || id || correo || "default";

      // Si hay caché válido, usarlo
      if (layoutCache.data && layoutCache.timestamp) {
        const age = Date.now() - layoutCache.timestamp;
        if (age < CACHE_DURATION) {
          //console.log(`✅ ${componentName} - Usando layout de caché (${Math.round(age / 1000)}s antiguo)`);
          if (isMounted.current) {
            setLayout(layoutCache.data);
            setLoading(false);
          }
          return;
        }
      }

      // Si ya hay una petición en curso, esperar a que termine
      if (layoutCache.loading && layoutCache.promise) {
        //console.log(`⏳ ${componentName} - Esperando petición en curso...`);
        try {
          const result = await layoutCache.promise;
          if (isMounted.current) {
            setLayout(result);
            setLoading(false);
          }
        } catch (error) {
          console.error(`❌ ${componentName} - Error al esperar petición:`, error);
          if (isMounted.current) {
            setLoading(false);
          }
        }
        return;
      }

      // Hacer nueva petición
      //console.log(`🌐 ${componentName} - Cargando layout desde API...`);
      layoutCache.loading = true;
      
      const fetchPromise = (async () => {
        try {
          const response = await getLayoutDashboard(identificador);

          const savedLayout = response.data?.data?.layout || response.data?.layout;
          
          // Guardar en caché
          layoutCache.data = savedLayout;
          layoutCache.timestamp = Date.now();
          layoutCache.loading = false;
          layoutCache.promise = null;
          
          //console.log(`✅ ${componentName} - Layout cargado y cacheado`);
          return savedLayout;
        } catch (error) {
          console.error(`❌ ${componentName} - Error al cargar layout:`, error);
          layoutCache.loading = false;
          layoutCache.promise = null;
          return null;
        }
      })();

      layoutCache.promise = fetchPromise;

      try {
        const result = await fetchPromise;
        if (isMounted.current) {
          setLayout(result);
          setLoading(false);
        }
      } catch (error) {
        if (isMounted.current) {
          setLoading(false);
        }
      }
    };

    loadLayout();
  }, [componentName]);

  return { layout, loading };
};

/**
 * Función para invalidar el caché (llamar después de guardar cambios)
 */
export const invalidateLayoutCache = () => {
  //console.log('🔄 Invalidando caché de layout');
  layoutCache.data = null;
  layoutCache.timestamp = null;
  layoutCache.loading = false;
  layoutCache.promise = null;
};
